import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  ApiResponse, 
  LoginCredentials, 
  AuthResponse, 
  User,
  Page,
  PageFormData,
  Service,
  ServiceFormData,
  GalleryImage,
  GalleryImageFormData,
  GalleryResponse,
  ContactSubmission,
  ContactStats,
  SettingsObject,
  DashboardStats
} from '../types';

class ApiService {
  private api: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'https://backend.edenspotgardens.com/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Load token from localStorage
    this.token = localStorage.getItem('admin_token');
    if (this.token) {
      this.setAuthToken(this.token);
    }

    // Add response interceptor for error handling
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.logout();
        }
        return Promise.reject(error);
      }
    );
  }

  // Utility method to get the base URL for images
  getImageUrl(path: string): string {
    // Extract the base URL without the /api suffix for images
    const baseUrl = this.api.defaults.baseURL?.replace('/api', '') || 'https://backend.edenspotgardens.com/api';
    return `${baseUrl}/${path}`;
  }

  private setAuthToken(token: string) {
    this.token = token;
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('admin_token', token);
  }

  private clearAuthToken() {
    this.token = null;
    delete this.api.defaults.headers.common['Authorization'];
    localStorage.removeItem('admin_token');
  }

  // Authentication
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response: AxiosResponse<ApiResponse<AuthResponse>> = await this.api.post('/auth/login', credentials);
    if (response.data.success && response.data.data) {
      this.setAuthToken(response.data.data.token);
      return response.data.data;
    }
    throw new Error(response.data.message || 'Login failed');
  }

  async getProfile(): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.get('/auth/profile');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to get profile');
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response: AxiosResponse<ApiResponse> = await this.api.put('/auth/change-password', {
      currentPassword,
      newPassword
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to change password');
    }
  }

  // User management (admin only)
  async getUsers(): Promise<User[]> {
    const response: AxiosResponse<ApiResponse<User[]>> = await this.api.get('/auth/users');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to get users');
  }

  async createUser(userData: { name: string; email: string; password: string; role?: 'admin' | 'editor' }): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.post('/auth/users', userData);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to create user');
  }

  async updateUser(id: number, userData: { name?: string; email?: string; role?: 'admin' | 'editor'; is_active?: boolean }): Promise<User> {
    const response: AxiosResponse<ApiResponse<User>> = await this.api.put(`/auth/users/${id}`, userData);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to update user');
  }

  async deleteUser(id: number): Promise<void> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/auth/users/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete user');
    }
  }

  async resetUserPassword(id: number, newPassword: string): Promise<void> {
    const response: AxiosResponse<ApiResponse> = await this.api.put(`/auth/users/${id}/reset-password`, {
      newPassword
    });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to reset password');
    }
  }

  logout() {
    this.clearAuthToken();
    window.location.href = '/login';
  }

  // Pages
  async getPages(): Promise<Page[]> {
    const response: AxiosResponse<ApiResponse<Page[]>> = await this.api.get('/pages');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch pages');
  }

  async getPage(slug: string): Promise<Page> {
    const response: AxiosResponse<ApiResponse<Page>> = await this.api.get(`/pages/${slug}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch page');
  }

  async createPage(data: PageFormData): Promise<Page> {
    const response: AxiosResponse<ApiResponse<Page>> = await this.api.post('/pages', data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to create page');
  }

  async updatePage(id: number, data: Partial<PageFormData>): Promise<Page> {
    const response: AxiosResponse<ApiResponse<Page>> = await this.api.put(`/pages/${id}`, data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to update page');
  }

  async deletePage(id: number): Promise<void> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/pages/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete page');
    }
  }

  // Services
  async getServices(): Promise<Service[]> {
    const response: AxiosResponse<ApiResponse<Service[]>> = await this.api.get('/services');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch services');
  }

  async getService(id: number): Promise<Service> {
    const response: AxiosResponse<ApiResponse<Service>> = await this.api.get(`/services/${id}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch service');
  }

  async createService(data: ServiceFormData): Promise<Service> {
    const response: AxiosResponse<ApiResponse<Service>> = await this.api.post('/services', data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to create service');
  }

  async updateService(id: number, data: Partial<ServiceFormData>): Promise<Service> {
    const response: AxiosResponse<ApiResponse<Service>> = await this.api.put(`/services/${id}`, data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to update service');
  }

  async deleteService(id: number): Promise<void> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/services/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete service');
    }
  }

  async reorderServices(serviceIds: number[]): Promise<void> {
    const response: AxiosResponse<ApiResponse> = await this.api.put('/services/reorder', { serviceIds });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to reorder services');
    }
  }

  async uploadServiceImage(serviceId: number, imageFile: File): Promise<{ image_filename: string; image_url: string }> {
    const formData = new FormData();
    formData.append('image', imageFile);

    const response: AxiosResponse<ApiResponse<{ id: number; image_filename: string; image_url: string }>> = 
      await this.api.post(`/services/${serviceId}/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

    if (response.data.success && response.data.data) {
      return {
        image_filename: response.data.data.image_filename,
        image_url: response.data.data.image_url
      };
    }
    throw new Error(response.data.message || 'Failed to upload image');
  }

  // Gallery
  async getGalleryImages(page = 1, limit = 20, category?: string, featured?: boolean): Promise<GalleryResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (category) params.append('category', category);
    if (featured !== undefined) params.append('featured', featured.toString());

    const response: AxiosResponse<ApiResponse<GalleryResponse>> = await this.api.get(`/gallery?${params}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch gallery images');
  }

  async getFeaturedImages(): Promise<GalleryImage[]> {
    const response: AxiosResponse<ApiResponse<GalleryImage[]>> = await this.api.get('/gallery/featured');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch featured images');
  }

  async uploadImage(file: File, caption?: string, category?: string, featured?: boolean, service_id?: number): Promise<GalleryImage> {
    const formData = new FormData();
    formData.append('image', file);
    if (caption) formData.append('caption', caption);
    if (category) formData.append('category', category);
    if (service_id) formData.append('service_id', service_id.toString());
    if (featured !== undefined) formData.append('featured', featured.toString());

    const response: AxiosResponse<ApiResponse<GalleryImage>> = await this.api.post('/gallery', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to upload image');
  }

  async updateImage(id: number, data: Partial<GalleryImageFormData>): Promise<GalleryImage> {
    const response: AxiosResponse<ApiResponse<GalleryImage>> = await this.api.put(`/gallery/${id}`, data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to update image');
  }

  async deleteImage(id: number): Promise<void> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/gallery/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete image');
    }
  }

  async reorderImages(imageIds: number[]): Promise<void> {
    const response: AxiosResponse<ApiResponse> = await this.api.put('/gallery/reorder', { imageIds });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to reorder images');
    }
  }

  async getImageCategories(): Promise<string[]> {
    const response: AxiosResponse<ApiResponse<string[]>> = await this.api.get('/gallery/categories');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch image categories');
  }

  async getServicesForCategories(): Promise<{ id: number; title: string; color_gradient?: string }[]> {
    const response: AxiosResponse<ApiResponse<{ id: number; title: string; color_gradient?: string }[]>> = await this.api.get('/gallery/services');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch services for categories');
  }

  // Contact
  async getContactSubmissions(page = 1, limit = 20, status?: string): Promise<{ submissions: ContactSubmission[], pagination: any }> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    if (status) params.append('status', status);

    const response: AxiosResponse<ApiResponse<{ submissions: ContactSubmission[], pagination: any }>> = await this.api.get(`/contact?${params}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch contact submissions');
  }

  async getContactSubmission(id: number): Promise<ContactSubmission> {
    const response: AxiosResponse<ApiResponse<ContactSubmission>> = await this.api.get(`/contact/${id}`);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch contact submission');
  }

  async updateContactSubmission(id: number, data: { status?: string; admin_notes?: string }): Promise<ContactSubmission> {
    const response: AxiosResponse<ApiResponse<ContactSubmission>> = await this.api.put(`/contact/${id}`, data);
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to update contact submission');
  }

  async deleteContactSubmission(id: number): Promise<void> {
    const response: AxiosResponse<ApiResponse> = await this.api.delete(`/contact/${id}`);
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete contact submission');
    }
  }

  async getContactStats(): Promise<ContactStats> {
    const response: AxiosResponse<ApiResponse<ContactStats>> = await this.api.get('/contact/stats');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch contact stats');
  }

  // Settings
  async getSettings(): Promise<SettingsObject> {
    const response: AxiosResponse<ApiResponse<SettingsObject>> = await this.api.get('/settings');
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || 'Failed to fetch settings');
  }

  async updateSetting(key: string, value: any): Promise<void> {
    const response: AxiosResponse<ApiResponse> = await this.api.put(`/settings/${key}`, { value });
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update setting');
    }
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      const response: AxiosResponse<ApiResponse> = await this.api.get('/health');
      return response.data.success;
    } catch {
      return false;
    }
  }
}

export const apiService = new ApiService();
export default apiService; 